import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { apiService } from '../services/apiService';
import TaskSubNav from '../components/TaskSubNav';
import TaskAttachments, { uploadPendingAttachments } from '../components/TaskAttachments';
import { showToast } from '../components/Toast';
import { userDisplayName } from '../utils/userDisplayName';
import { DSBackLink, DSButton, DSInput, DSSelect, DSTextarea, DSCard, DSIcon, DSTypeaheadFilterSelect } from '../design-system/primitives';
import './taskManagementShared.css';

const TASK_TYPES = [
  { key: 'one_off', icon: <DSIcon name="target" />, label: 'One-off', description: 'Single task, not repeated' },
  { key: 'auto_recurring', icon: <DSIcon name="refresh" />, label: 'Auto-recurring', description: 'System creates on schedule' },
  { key: 'on_demand_recurring', icon: <DSIcon name="clipboard" />, label: 'On-demand recurring', description: 'Template for manual spawning' }
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function TaskForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isTemplateRoute = location.pathname.includes('/templates/');
  const isEditing = Boolean(id);

  const [taskType, setTaskType] = useState(isTemplateRoute ? 'on_demand_recurring' : 'one_off');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pointValue, setPointValue] = useState(1);
  const [bountyEnabled, setBountyEnabled] = useState(false);
  const [bountyAmount, setBountyAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [defaultDueOffsetDays, setDefaultDueOffsetDays] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [recurrencePattern, setRecurrencePattern] = useState('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [generateAdvanceDays, setGenerateAdvanceDays] = useState(7);
  const [pendingScreenshots, setPendingScreenshots] = useState([]);

  const [categories, setCategories] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isTemplateType = taskType === 'auto_recurring' || taskType === 'on_demand_recurring';

  const assigneeOptions = useMemo(
    () => assignableUsers.map((u) => ({ value: u.id, label: userDisplayName(u) })),
    [assignableUsers]
  );

  useEffect(() => {
    apiService.getTaskCategories().then((res) => setCategories(res.data || [])).catch(() => {});
    apiService.getTaskAssignableUsers().then((res) => setAssignableUsers(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    const loader = isTemplateRoute ? apiService.getTaskTemplate(id) : apiService.getTask(id);
    loader.then((res) => {
      const data = res.data;
      setTitle(data.title || '');
      setDescription(data.description || '');
      setPointValue(data.point_value || 1);
      const bounty = isTemplateRoute ? data.default_bounty_amount : data.bounty_amount;
      setBountyEnabled(!!bounty);
      setBountyAmount(bounty || '');
      setCategoryId(data.category_id || '');
      if (isTemplateRoute) {
        setTaskType(data.template_type === 'auto_recurring' ? 'auto_recurring' : 'on_demand_recurring');
        setDefaultDueOffsetDays(data.default_due_offset_days ?? '');
        setRecurrencePattern(data.recurrence_pattern || 'weekly');
        setDayOfWeek(data.day_of_week ?? 1);
        setGenerateAdvanceDays(data.generate_advance_days || 7);
      } else {
        setTaskType('one_off');
        setDueDate(data.due_date ? data.due_date.slice(0, 10) : '');
        setAssigneeIds((data.assignees || []).map((a) => a.userId));
      }
    })
      .catch((err) => setError(err.response?.data?.error || 'Unable to load'))
      .finally(() => setLoading(false));
  }, [id, isEditing, isTemplateRoute]);

  // The task or template is already saved by now, so a failed upload is reported
  // rather than blocking: the screenshots can be added again from the edit page.
  const attachPendingScreenshots = async (owner, ownerId) => {
    try {
      await uploadPendingAttachments(owner, ownerId, pendingScreenshots);
    } catch (err) {
      showToast(err.response?.data?.error || 'Saved, but the screenshots could not be attached. Try adding them again.', 'error');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!title.trim()) {
      setError('Task title is required');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      if (isTemplateType) {
        const payload = {
          title,
          description,
          templateType: taskType === 'auto_recurring' ? 'auto_recurring' : 'on_demand',
          categoryId: categoryId || null,
          pointValue,
          defaultBountyAmount: bountyEnabled ? Number(bountyAmount) : null,
          defaultDueOffsetDays: defaultDueOffsetDays !== '' ? Number(defaultDueOffsetDays) : null
        };
        if (taskType === 'auto_recurring') {
          payload.recurrencePattern = recurrencePattern;
          payload.dayOfWeek = ['weekly', 'biweekly'].includes(recurrencePattern) ? dayOfWeek : null;
          payload.generateAdvanceDays = generateAdvanceDays;
        }

        if (isEditing) {
          await apiService.updateTaskTemplate(id, payload);
        } else {
          const response = await apiService.createTaskTemplate(payload);
          await attachPendingScreenshots('template', response.data.id);
        }
        navigate('/tasks/templates', { replace: true });
      } else {
        const payload = {
          title,
          description,
          pointValue,
          bountyAmount: bountyEnabled ? Number(bountyAmount) : null,
          dueDate: dueDate || null,
          categoryId: categoryId || null
        };

        if (isEditing) {
          await apiService.updateTask(id, payload);
          navigate(`/tasks/${id}`, { replace: true });
        } else {
          payload.assigneeUserIds = assigneeIds;
          const response = await apiService.createTask(payload);
          await attachPendingScreenshots('task', response.data.id);
          navigate(`/tasks/${response.data.id}`, { replace: true });
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save task');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-inline">Loading...</div>;
  }

  return (
    <div className="tm-page">
      <DSBackLink onClick={() => navigate(-1)}>Back</DSBackLink>
      <div className="tm-section">
        <h1>
          {isTemplateRoute
            ? (isEditing ? 'Edit Template' : 'New Template')
            : (isEditing ? 'Edit Task' : 'Create Task')}
        </h1>
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="tm-form-group">
            <DSInput label="Task Title *" type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>

          <div className="tm-form-group">
            <DSTextarea label="Description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="tm-form-group">
            {isEditing ? (
              <TaskAttachments owner={isTemplateRoute ? 'template' : 'task'} ownerId={id} />
            ) : (
              <TaskAttachments pendingFiles={pendingScreenshots} onPendingChange={setPendingScreenshots} />
            )}
            {isTemplateType && (
              <div className="tm-suggestion-meta">Screenshots on a template are copied onto every task it creates.</div>
            )}
          </div>

          {isEditing && !isTemplateRoute ? (
            <div className="tm-form-group">
              <label>Task Type</label>
              <div className="tm-suggestion-meta">
                One-off task. A task instance's type can't be changed after it's created
                (this is separate from templates, which live in the Template Library).
              </div>
            </div>
          ) : (
            <div className="tm-form-group">
              <label>Task Type *</label>
              {TASK_TYPES.map((type) => (
                <DSCard
                  key={type.key}
                  variant="elevated"
                  bgVariant="default"
                  borderVariant="subtle"
                  cornerVariant="lg"
                  className={`tm-radio-card ${taskType === type.key ? 'selected' : ''}`}
                  onClick={() => setTaskType(type.key)}
                >
                  <input type="radio" checked={taskType === type.key} onChange={() => setTaskType(type.key)} />
                  <div>
                    <strong>{type.icon} {type.label}</strong>
                    <div>{type.description}</div>
                  </div>
                </DSCard>
              ))}
            </div>
          )}

          <div className="tm-form-row">
            <div className="tm-form-group">
              <DSInput label="Points *" type="number" min={1} value={pointValue} onChange={(e) => setPointValue(Number(e.target.value))} required />
            </div>
            <div className="tm-form-group">
              <label>
                <input type="checkbox" checked={bountyEnabled} onChange={(e) => setBountyEnabled(e.target.checked)} />
                {' '}Bounty (credits)
              </label>
              {bountyEnabled && (
                <DSInput type="number" min={0} step="0.01" value={bountyAmount} onChange={(e) => setBountyAmount(e.target.value)} />
              )}
            </div>
          </div>

          <div className="tm-form-group">
            <DSSelect label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">None</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </DSSelect>
          </div>

          {!isTemplateType && (
            <>
              <div className="tm-form-group">
                <DSInput label="Due Date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
              {isEditing ? (
                <div className="tm-form-group">
                  <label>Assignee(s)</label>
                  <div className="tm-suggestion-meta">
                    Use "Assign / Reassign" on the task page to change who's assigned.
                  </div>
                </div>
              ) : (
                <div className="tm-form-group">
                  <DSTypeaheadFilterSelect
                    id="task-assignees"
                    label="Assignee(s) (leave empty for open pool)"
                    placeholder="Search members by name..."
                    emptyMessage="No members found"
                    options={assigneeOptions}
                    selectedValues={assigneeIds}
                    onChange={setAssigneeIds}
                  />
                  {categoryId && categories.find((c) => String(c.id) === String(categoryId))?.default_assignee_name && (
                    <div className="tm-suggestion-meta">
                      This category assigns to {categories.find((c) => String(c.id) === String(categoryId)).default_assignee_name} by
                      default if you leave this empty.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {isTemplateType && (
            <div className="tm-form-group">
              <DSInput label="Due N days after being spawned" type="number" min={0} value={defaultDueOffsetDays} onChange={(e) => setDefaultDueOffsetDays(e.target.value)} />
            </div>
          )}

          {taskType === 'auto_recurring' && (
            <>
              <div className="tm-form-group">
                <DSSelect label="Recurrence Pattern" value={recurrencePattern} onChange={(e) => setRecurrencePattern(e.target.value)}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="monthly">Monthly</option>
                </DSSelect>
              </div>
              {['weekly', 'biweekly'].includes(recurrencePattern) && (
                <div className="tm-form-group">
                  <DSSelect label="Day of Week" value={dayOfWeek} onChange={(e) => setDayOfWeek(Number(e.target.value))}>
                    {DAY_NAMES.map((name, idx) => <option key={idx} value={idx}>{name}</option>)}
                  </DSSelect>
                </div>
              )}
              <div className="tm-form-group">
                <DSInput label="Generate Advance Days" type="number" min={1} value={generateAdvanceDays} onChange={(e) => setGenerateAdvanceDays(Number(e.target.value))} />
              </div>
            </>
          )}

          <DSButton type="submit" appearance="solid" tone="primary" disabled={submitting} leadingIcon={<DSIcon name="save" />}>{submitting ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Task')}
          </DSButton>
        </form>
      </div>
      <TaskSubNav />
    </div>
  );
}

export default TaskForm;
